import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calculator, Calendar, CheckCircle2, ChevronRight, Download, FileText, Play, X } from 'lucide-react';
import { AppShell } from '@/components/app/AppShell';
import {
  NenchoCycle, NenchoInput, NenchoResult, NenchoStatus,
  useComputeNencho, useNenchoCycles, useNenchoInputs, useNenchoResults,
  useLastNenchoInput, useOpenNenchoCycle, useSetNenchoCycleStatus, useSubmitNenchoInput,
  gensenPdfUrl, hoteiChoshoPdfUrl,
} from '@/lib/nencho';
import { useEmployees, type Employee } from '@/lib/employees';

const STATUS_COLOR: Record<string, string> = {
  open:       'bg-sky-500/10 text-sky-700 dark:text-sky-300',
  collecting: 'bg-amber-500/10 text-amber-700 dark:text-amber-300',
  computing:  'bg-violet-500/10 text-violet-700 dark:text-violet-300',
  closed:     'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
};

const yen = (cents: number) => `¥${Math.round(cents / 100).toLocaleString('ja-JP')}`;

export function YearEndPage() {
  const { t } = useTranslation();
  const cycles = useNenchoCycles();
  const employees = useEmployees({ country: 'JP' });
  const openCycle = useOpenNenchoCycle();
  const setCycleStatus = useSetNenchoCycleStatus();
  const [selectedCycle, setSelectedCycle] = useState<NenchoCycle | null>(null);
  const results = useNenchoResults(selectedCycle?.id ?? null);
  const inputs = useNenchoInputs(selectedCycle?.id ?? null);
  const compute = useComputeNencho();
  const [submitFor, setSubmitFor] = useState<{ employee: Employee; existing: NenchoInput | null } | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  const inputByEmployee = useMemo(() => {
    const map = new Map<string, NenchoInput>();
    for (const i of inputs.data ?? []) map.set(i.employeeId, i);
    return map;
  }, [inputs.data]);

  const submittedCount = (inputs.data ?? []).filter((i) => i.submittedAt != null).length;
  const employeeCount = employees.data?.length ?? 0;
  const ready = submittedCount > 0;

  const runCompute = async () => {
    if (!selectedCycle) return;
    setFeedback(null);
    try {
      const out = await compute.mutateAsync(selectedCycle.id);
      setFeedback(
        out.results.length === 0
          ? t('yearEnd.feedback.empty', { defaultValue: 'No submitted declarations to compute. Submit at least one declaration first.' })
          : t('yearEnd.feedback.done', { n: out.results.length, defaultValue: `Computed ${out.results.length} declaration${out.results.length === 1 ? '' : 's'}.` }),
      );
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
        || (err as { message?: string })?.message
        || 'Failed to compute';
      setFeedback(msg);
    }
  };

  return (
    <AppShell>
      <div className="px-6 md:px-10 py-8 max-w-7xl mx-auto space-y-6">
        <header>
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">{t('yearEnd.eyebrow')}</p>
          <h1 className="display serif-jp text-3xl md:text-4xl font-semibold tracking-tight">{t('yearEnd.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground max-w-2xl text-pretty">{t('yearEnd.subtitle')}</p>
        </header>

        <section className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2 rounded-2xl border border-border bg-card shadow-soft">
            <div className="px-5 py-3 border-b border-border bg-muted/30 flex items-center justify-between">
              <h2 className="text-sm font-semibold">{t('yearEnd.cycles')}</h2>
              <OpenNewCycleButton onOpen={openCycle.mutateAsync} pending={openCycle.isPending} />
            </div>
            {(cycles.data ?? []).length === 0 ? (
              <p className="px-5 py-12 text-center text-sm text-muted-foreground">{t('yearEnd.emptyCycles')}</p>
            ) : (
              <ul className="divide-y divide-border">
                {(cycles.data ?? []).map((c, i) => (
                  <motion.li
                    key={c.id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04 }}
                    className={`px-5 py-3 flex items-center gap-3 hover:bg-muted/20 transition cursor-pointer ${selectedCycle?.id === c.id ? 'bg-primary/5' : ''}`}
                    onClick={() => setSelectedCycle(c)}
                  >
                    <span className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white">
                      <Calendar className="h-4 w-4" />
                    </span>
                    <div className="flex-1">
                      <p className="text-sm font-semibold num">{c.year} {t('yearEnd.cycle')}</p>
                      {c.collectionDeadline && (
                        <p className="text-xs text-muted-foreground">
                          {t('yearEnd.collectionBy', { date: c.collectionDeadline })}
                        </p>
                      )}
                    </div>
                    <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-md ${STATUS_COLOR[c.status] ?? 'bg-muted'}`}>
                      {t(`yearEnd.status.${c.status}`)}
                    </span>
                    <AdvanceCycleButton cycle={c} onAdvance={setCycleStatus.mutateAsync} pending={setCycleStatus.isPending} />
                  </motion.li>
                ))}
              </ul>
            )}
          </div>

          <aside className="rounded-2xl border border-border bg-card p-5 shadow-soft">
            <h2 className="text-sm font-semibold mb-3">{t('yearEnd.summary.title')}</h2>
            <p className="text-xs text-muted-foreground mb-3">{t('yearEnd.summary.body')}</p>
            <ul className="text-xs text-foreground/80 space-y-1.5">
              <li>· {t('yearEnd.summary.step1')}</li>
              <li>· {t('yearEnd.summary.step2')}</li>
              <li>· {t('yearEnd.summary.step3')}</li>
              <li>· {t('yearEnd.summary.step4')}</li>
            </ul>
            <p className="num text-[11px] text-muted-foreground mt-3">
              {t('yearEnd.summary.jpEmployees', { n: employeeCount })}
            </p>
          </aside>
        </section>

        {selectedCycle && (
          <>
            <section className="rounded-2xl border border-border bg-card shadow-soft">
              <div className="px-5 py-3 border-b border-border bg-muted/30 flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-sm font-semibold num">{t('yearEnd.declarations.title', { defaultValue: `Declarations · ${selectedCycle.year}`, year: selectedCycle.year })}</h2>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    {t('yearEnd.declarations.progress', {
                      submitted: submittedCount,
                      total: employeeCount,
                      defaultValue: `${submittedCount} of ${employeeCount} JP employees have submitted their year-end declarations.`,
                    })}
                  </p>
                </div>
                <div className="text-[11px] font-semibold rounded-full px-3 py-1 bg-muted/50">
                  {submittedCount} / {employeeCount}
                </div>
              </div>
              {employeeCount === 0 ? (
                <p className="px-5 py-10 text-center text-sm text-muted-foreground">
                  {t('yearEnd.declarations.noJpEmployees', { defaultValue: 'No JP employees yet. Add an employee in People with country = JP to start collecting declarations.' })}
                </p>
              ) : (
                <ul className="divide-y divide-border">
                  {(employees.data ?? []).map((e) => {
                    const existing = inputByEmployee.get(e.id) ?? null;
                    const submitted = !!existing?.submittedAt;
                    return (
                      <li key={e.id} className="px-5 py-3 flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{e.legalNameLocal}</p>
                          <p className="text-[11px] text-muted-foreground truncate">{e.email}</p>
                        </div>
                        {submitted ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            {t('yearEnd.declarations.submitted', { defaultValue: 'Submitted' })}
                          </span>
                        ) : (
                          <span className="text-[11px] text-muted-foreground">{t('yearEnd.declarations.notSubmitted', { defaultValue: 'Not submitted' })}</span>
                        )}
                        <button
                          type="button"
                          onClick={() => setSubmitFor({ employee: e, existing })}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-xs font-semibold hover:bg-muted transition"
                        >
                          <FileText className="h-3 w-3" />
                          {existing
                            ? t('yearEnd.declarations.edit', { defaultValue: 'Edit' })
                            : t('yearEnd.declarations.submit', { defaultValue: 'Submit declaration' })}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            <section className="rounded-2xl border border-border bg-card shadow-soft">
              <div className="px-5 py-3 border-b border-border bg-muted/30 flex items-center justify-between">
                <h2 className="text-sm font-semibold num">{t('yearEnd.results.title', { year: selectedCycle.year })}</h2>
                <div className="flex items-center gap-2">
                  {(results.data ?? []).length > 0 && (
                    <a
                      href={hoteiChoshoPdfUrl(selectedCycle.id)}
                      target="_blank"
                      rel="noopener"
                      className="inline-flex h-9 items-center gap-1.5 px-3 rounded-lg border border-border text-xs font-semibold hover:bg-muted/40 transition"
                    >
                      <Download className="h-3.5 w-3.5" />{t('yearEnd.results.hoteiPdf', { defaultValue: '法定調書合計表 PDF' })}
                    </a>
                  )}
                  <button
                    onClick={runCompute}
                    disabled={compute.isPending || !ready}
                    title={!ready ? t('yearEnd.results.noInputsTooltip', { defaultValue: 'Submit at least one declaration first' }) : undefined}
                    className="gradient-primary inline-flex h-9 items-center gap-1.5 px-4 rounded-lg text-white text-xs font-semibold shadow-glow disabled:opacity-60 transition"
                  >
                    {compute.isPending ? (
                      <><span className="h-3 w-3 rounded-full border-2 border-white/30 border-t-white animate-spin" />{t('yearEnd.computing')}</>
                    ) : (
                      <><Calculator className="h-3.5 w-3.5" />{t('yearEnd.runCompute')}</>
                    )}
                  </button>
                </div>
              </div>

              {feedback && (
                <div className="px-5 py-3 border-b border-border bg-muted/10 text-xs text-foreground/80 flex items-start justify-between gap-3">
                  <span>{feedback}</span>
                  <button type="button" onClick={() => setFeedback(null)} aria-label="dismiss" className="text-muted-foreground hover:text-foreground">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}

              {(results.data ?? []).length === 0 ? (
                <p className="px-5 py-12 text-center text-sm text-muted-foreground">
                  {t('yearEnd.results.empty')}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-muted/40 text-start">
                      <tr>
                        <th className="px-4 py-3 font-semibold">{t('yearEnd.results.employee')}</th>
                        <th className="px-4 py-3 font-semibold">{t('yearEnd.results.annualGross')}</th>
                        <th className="px-4 py-3 font-semibold">{t('yearEnd.results.deductions')}</th>
                        <th className="px-4 py-3 font-semibold">{t('yearEnd.results.computedTax')}</th>
                        <th className="px-4 py-3 font-semibold">{t('yearEnd.results.withheld')}</th>
                        <th className="px-4 py-3 font-semibold">{t('yearEnd.results.adjustment')}</th>
                        <th className="px-4 py-3 font-semibold">{t('yearEnd.results.gensen', { defaultValue: '源泉徴収票' })}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {(results.data ?? []).map((r) => <ResultRow key={r.id} r={r} cycleId={selectedCycle.id} />)}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </div>

      {submitFor && selectedCycle && (
        <SubmitNenchoInputDialog
          cycleId={selectedCycle.id}
          employee={submitFor.employee}
          existing={submitFor.existing}
          onClose={() => setSubmitFor(null)}
        />
      )}
    </AppShell>
  );
}

function ResultRow({ r, cycleId }: { r: NenchoResult; cycleId: string }) {
  const { t } = useTranslation();
  const refund = r.adjustmentCents > 0;
  return (
    <tr className="hover:bg-muted/20 transition">
      <td className="px-4 py-3 num text-xs">
        <Link
          to={`/app/people/${r.employeeId}`}
          className="underline decoration-dotted hover:text-primary transition-colors"
          onClick={(e) => e.stopPropagation()}
        >
          {r.employeeId.slice(0, 8)}…
        </Link>
      </td>
      <td className="px-4 py-3 num">{yen(r.annualGrossCents)}</td>
      <td className="px-4 py-3 num text-muted-foreground">−{yen(r.totalDeductionsCents)}</td>
      <td className="px-4 py-3 num">{yen(r.computedAnnualTaxCents)}</td>
      <td className="px-4 py-3 num text-muted-foreground">{yen(r.withheldTotalCents)}</td>
      <td className={`px-4 py-3 num font-semibold ${refund ? 'text-emerald-600' : 'text-rose-600'}`}>
        {refund ? '+' : ''}{yen(r.adjustmentCents)}
        <span className="ms-1 text-[10px] uppercase tracking-wider text-muted-foreground">
          {refund ? t('yearEnd.results.refund') : t('yearEnd.results.additional')}
        </span>
      </td>
      <td className="px-4 py-3">
        <a
          href={gensenPdfUrl(cycleId, r.employeeId)}
          target="_blank"
          rel="noopener"
          className="inline-flex items-center gap-1 text-accent-700 hover:text-accent-600 text-[11px] underline font-semibold"
        >
          <Download className="h-3 w-3" />PDF
        </a>
      </td>
    </tr>
  );
}

const NEXT_STATUS: Partial<Record<NenchoStatus, NenchoStatus>> = {
  open:       'collecting',
  collecting: 'computing',
  computing:  'closed',
};

function AdvanceCycleButton({
  cycle, onAdvance, pending,
}: {
  cycle: NenchoCycle;
  onAdvance: (args: { id: string; status: NenchoStatus }) => Promise<unknown>;
  pending: boolean;
}) {
  const { t } = useTranslation();
  const next = NEXT_STATUS[cycle.status];
  if (!next) return null;

  const label: Record<string, string> = {
    collecting: t('yearEnd.advance.startCollecting', { defaultValue: 'Start collecting' }),
    computing:  t('yearEnd.advance.startComputing',  { defaultValue: 'Start computing' }),
    closed:     t('yearEnd.advance.closeCycle',      { defaultValue: 'Close cycle' }),
  };

  return (
    <button
      type="button"
      disabled={pending}
      onClick={(e) => {
        e.stopPropagation();
        void onAdvance({ id: cycle.id, status: next });
      }}
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-border text-[11px] font-semibold hover:bg-muted transition disabled:opacity-60 shrink-0"
    >
      <ChevronRight className="h-3 w-3" />
      {label[next]}
    </button>
  );
}

function OpenNewCycleButton({
  onOpen, pending,
}: { onOpen: (args: { year: number; collectionDeadline?: string }) => Promise<unknown>; pending: boolean }) {
  const { t } = useTranslation();
  const year = new Date().getFullYear();
  return (
    <button
      onClick={() => void onOpen({ year, collectionDeadline: `${year}-12-15` })}
      disabled={pending}
      className="inline-flex h-9 items-center gap-1.5 px-3 rounded-lg border border-border text-xs font-semibold hover:bg-muted transition disabled:opacity-60"
    >
      <Play className="h-3.5 w-3.5" />
      {pending ? t('common.saving') : t('yearEnd.openYear', { year })}
    </button>
  );
}

/**
 * HR-side submission form for a year-end declaration on behalf of an
 * employee. Mirrors the typical 給与所得者の保険料控除申告書 fields:
 * dependents + insurance + iDeCo + mortgage credit. Submitted inputs
 * become eligible for `compute()`; drafts are skipped.
 */
// Wrapper: when there's no declaration for THIS cycle yet, carry forward the
// employee's most recent prior-year declaration so they don't re-enter it.
function SubmitNenchoInputDialog({
  cycleId, employee, existing, onClose,
}: {
  cycleId: string;
  employee: Employee;
  existing: NenchoInput | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const carry = useLastNenchoInput(cycleId, employee.id, !existing);
  if (!existing && carry.isLoading) {
    return (
      <div className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-sm grid place-items-center p-4">
        <div className="bg-white dark:bg-card rounded-2xl shadow-glow border border-border px-6 py-5 text-sm text-muted-foreground">
          {t('common.loading')}
        </div>
      </div>
    );
  }
  const seed = existing ?? carry.data ?? null;
  return (
    <NenchoInputForm
      cycleId={cycleId}
      employee={employee}
      existing={seed}
      carriedForward={!existing && !!carry.data}
      onClose={onClose}
    />
  );
}

function NenchoInputForm({
  cycleId, employee, existing, carriedForward, onClose,
}: {
  cycleId: string;
  employee: Employee;
  existing: NenchoInput | null;
  carriedForward?: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const submit = useSubmitNenchoInput();

  const [spouseDependent, setSpouseDependent] = useState(existing?.spouseDependent ?? false);
  const [childDependents, setChildDependents] = useState<string>(String(existing?.childDependents ?? 0));
  const [otherDependents, setOtherDependents] = useState<string>(String(existing?.otherDependents ?? 0));
  const [lifeGeneral, setLifeGeneral] = useState<string>(toYenStr(existing?.lifeInsuranceGeneralCents));
  const [lifePension, setLifePension] = useState<string>(toYenStr(existing?.lifeInsurancePensionCents));
  const [lifeCare, setLifeCare] = useState<string>(toYenStr(existing?.lifeInsuranceCareCents));
  const [earthquake, setEarthquake] = useState<string>(toYenStr(existing?.earthquakeInsuranceCents));
  const [socialOther, setSocialOther] = useState<string>(toYenStr(existing?.socialInsuranceOtherCents));
  const [ideco, setIdeco] = useState<string>(toYenStr(existing?.idecoCents));
  const [mortgage, setMortgage] = useState<string>(toYenStr(existing?.mortgageCreditCents));
  // Phase 2B deductions
  const [spouseIncome, setSpouseIncome] = useState<string>(toYenStr(existing?.spouseAnnualIncomeCents ?? undefined));
  const [disGeneral, setDisGeneral] = useState<string>(String(existing?.disabilityGeneralCount ?? 0));
  const [disSpecial, setDisSpecial] = useState<string>(String(existing?.disabilitySpecialCount ?? 0));
  const [disCohabiting, setDisCohabiting] = useState<string>(String(existing?.disabilityCohabitingSpecialCount ?? 0));
  const [singleParent, setSingleParent] = useState(existing?.isSingleParent ?? false);
  const [widow, setWidow] = useState(existing?.isWidow ?? false);
  const [workingStudent, setWorkingStudent] = useState(existing?.isWorkingStudent ?? false);
  const [notes, setNotes] = useState<string>(existing?.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await submit.mutateAsync({
        cycleId,
        payload: {
          employeeId: employee.id,
          spouseDependent,
          childDependents: nonNegInt(childDependents),
          otherDependents: nonNegInt(otherDependents),
          lifeInsuranceGeneralCents: yenToCents(lifeGeneral),
          lifeInsurancePensionCents: yenToCents(lifePension),
          lifeInsuranceCareCents:    yenToCents(lifeCare),
          earthquakeInsuranceCents:  yenToCents(earthquake),
          socialInsuranceOtherCents: yenToCents(socialOther),
          idecoCents:                yenToCents(ideco),
          mortgageCreditCents:       yenToCents(mortgage),
          spouseAnnualIncomeCents:   spouseIncome.trim() === '' ? null : yenToCents(spouseIncome),
          disabilityGeneralCount:    nonNegInt(disGeneral),
          disabilitySpecialCount:    nonNegInt(disSpecial),
          disabilityCohabitingSpecialCount: nonNegInt(disCohabiting),
          isSingleParent:            singleParent,
          isWidow:                   widow,
          isWorkingStudent:          workingStudent,
          notes: notes.trim() || undefined,
        },
      });
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message
        || (err as { message?: string })?.message
        || 'Failed to submit declaration';
      setError(Array.isArray(msg) ? msg[0] : msg);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSubmit}
        className="bg-white dark:bg-card rounded-2xl shadow-glow border border-border max-w-xl w-full p-6 max-h-[90vh] overflow-y-auto keenez-scrollbar space-y-4"
      >
        <header>
          <p className="text-[11px] uppercase tracking-[0.2em] text-primary font-semibold">
            {t('yearEnd.form.eyebrow', { defaultValue: '年末調整 申告書' })}
          </p>
          <h2 className="text-lg font-semibold mt-1">
            {t('yearEnd.form.title', { defaultValue: 'Submit year-end declaration' })}
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            <strong>{employee.legalNameLocal}</strong> · {employee.email}
          </p>
        </header>

        {carriedForward && (
          <div className="rounded-lg border border-primary/30 bg-primary/5 text-primary text-xs px-3 py-2">
            {t('yearEnd.form.carriedForward', { defaultValue: '↩ Carried forward from last year’s declaration — review and update each figure before submitting.' })}
          </div>
        )}

        <fieldset className="space-y-3 border-t border-border pt-4">
          <legend className="text-xs font-semibold text-foreground">
            {t('yearEnd.form.dependents', { defaultValue: '扶養親族 / Dependents' })}
          </legend>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={spouseDependent}
              onChange={(e) => setSpouseDependent(e.target.checked)}
              className="accent-primary"
            />
            <span>{t('yearEnd.form.spouseDependent', { defaultValue: '配偶者控除 (Spouse is a dependent)' })}</span>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <NumberField label={t('yearEnd.form.childDependents', { defaultValue: 'Children dependents' })} value={childDependents} onChange={setChildDependents} />
            <NumberField label={t('yearEnd.form.otherDependents', { defaultValue: 'Other dependents' })} value={otherDependents} onChange={setOtherDependents} />
          </div>
        </fieldset>

        <fieldset className="space-y-3 border-t border-border pt-4">
          <legend className="text-xs font-semibold text-foreground">
            {t('yearEnd.form.insurance', { defaultValue: '生命保険料控除 / Insurance premiums (¥)' })}
          </legend>
          <div className="grid grid-cols-2 gap-3">
            <NumberField label="生命保険 (一般) / Life — general" value={lifeGeneral} onChange={setLifeGeneral} step={1000} />
            <NumberField label="生命保険 (年金) / Life — pension" value={lifePension} onChange={setLifePension} step={1000} />
            <NumberField label="生命保険 (介護) / Life — care" value={lifeCare} onChange={setLifeCare} step={1000} />
            <NumberField label="地震保険 / Earthquake" value={earthquake} onChange={setEarthquake} step={1000} />
            <NumberField label="社会保険料 (その他) / Social (other)" value={socialOther} onChange={setSocialOther} step={1000} />
            <NumberField label="iDeCo / 小規模企業共済" value={ideco} onChange={setIdeco} step={1000} />
          </div>
        </fieldset>

        <fieldset className="space-y-3 border-t border-border pt-4">
          <legend className="text-xs font-semibold text-foreground">
            {t('yearEnd.form.credits', { defaultValue: '住宅借入金等特別控除 / Other credits' })}
          </legend>
          <NumberField label="住宅借入金等特別控除 / Mortgage credit" value={mortgage} onChange={setMortgage} step={1000} />
        </fieldset>

        <fieldset className="space-y-3 border-t border-border pt-4">
          <legend className="text-xs font-semibold text-foreground">
            {t('yearEnd.form.spouseSpecial', { defaultValue: '配偶者控除 / Spouse income (¥, leave blank if no spouse)' })}
          </legend>
          <NumberField label="配偶者の合計所得 / Spouse total income" value={spouseIncome} onChange={setSpouseIncome} step={10000} />
          <p className="text-[11px] text-muted-foreground">
            {t('yearEnd.form.spouseHint', { defaultValue: '配偶者(特別)控除を所得に応じて自動判定します (≤¥480k→¥380k, 段階的に逓減)。' })}
          </p>
        </fieldset>

        <fieldset className="space-y-3 border-t border-border pt-4">
          <legend className="text-xs font-semibold text-foreground">
            {t('yearEnd.form.disability', { defaultValue: '障害者控除 / Disability (number of persons)' })}
          </legend>
          <div className="grid grid-cols-3 gap-3">
            <NumberField label="一般 / General" value={disGeneral} onChange={setDisGeneral} />
            <NumberField label="特別 / Special" value={disSpecial} onChange={setDisSpecial} />
            <NumberField label="同居特別 / Cohabiting" value={disCohabiting} onChange={setDisCohabiting} />
          </div>
        </fieldset>

        <fieldset className="space-y-2 border-t border-border pt-4">
          <legend className="text-xs font-semibold text-foreground">
            {t('yearEnd.form.otherDeductions', { defaultValue: 'その他の控除 / Other' })}
          </legend>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={singleParent} onChange={(e) => setSingleParent(e.target.checked)} className="accent-primary" /><span>ひとり親控除 / Single parent (¥350k)</span></label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={widow} onChange={(e) => setWidow(e.target.checked)} className="accent-primary" /><span>寡婦控除 / Widow (¥270k)</span></label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={workingStudent} onChange={(e) => setWorkingStudent(e.target.checked)} className="accent-primary" /><span>勤労学生控除 / Working student (¥270k)</span></label>
        </fieldset>

        <label className="block">
          <span className="block text-xs font-semibold text-foreground mb-1">
            {t('yearEnd.form.notes', { defaultValue: 'Notes (optional)' })}
          </span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
          />
        </label>

        {error && (
          <div className="rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-sm px-3 py-2">{error}</div>
        )}

        <div className="flex justify-end gap-2 pt-2 sticky bottom-0 bg-card/95 backdrop-blur-sm pb-1">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm font-semibold text-foreground hover:bg-muted rounded-lg transition">
            Cancel
          </button>
          <button
            type="submit"
            disabled={submit.isPending}
            className="gradient-primary inline-flex h-10 items-center gap-1.5 px-5 rounded-xl text-white text-sm font-semibold shadow-glow disabled:opacity-60 transition"
          >
            {submit.isPending
              ? t('yearEnd.form.submitting', { defaultValue: 'Submitting…' })
              : t('yearEnd.form.submit', { defaultValue: 'Submit declaration' })}
          </button>
        </div>
      </form>
    </div>
  );
}

function NumberField({
  label, value, onChange, step,
}: { label: string; value: string; onChange: (v: string) => void; step?: number }) {
  return (
    <label className="block">
      <span className="block text-[11px] text-muted-foreground mb-1">{label}</span>
      <input
        type="number"
        min="0"
        step={step ?? 1}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none num"
      />
    </label>
  );
}

function toYenStr(cents: number | undefined | null): string {
  if (cents == null) return '0';
  return String(Math.round(cents / 100));
}
function yenToCents(s: string): number {
  const n = Number(s);
  if (!isFinite(n) || n < 0) return 0;
  return Math.round(n * 100);
}
function nonNegInt(s: string): number {
  const n = Math.floor(Number(s));
  if (!isFinite(n) || n < 0) return 0;
  return n;
}
